import { API_TYPES } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import type { HiringFeedsResponse } from "@src/appV2/redesign/HiringFeed/api/hiringFeed.schema";
import { fetchHiringFeedApiUrl } from "@src/appV2/redesign/HiringFeed/api/usePaginatedHiringFeed";
import {
  type DiscoveryPlacementsResponse,
  fetchDiscoveryPlacementsApiUrl,
} from "@src/appV2/redesign/Placements/api/useFetchDiscoveryPlacements";
import { fetchPlacementsApiUrl } from "@src/appV2/redesign/Placements/api/useFetchPaginatedPlacements";
import type {
  GetPlacementsResponse,
  PlacementResponse,
} from "@src/appV2/redesign/Placements/types/fetchPlacements.schema";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";

import { getPlacementUrl } from "./useGetPlacement";

export async function invalidatePlacementFetchingQueries(
  queryClient: QueryClient,
  workerId: string,
  placementId: string
) {
  await queryClient.invalidateQueries({
    queryKey: [
      `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/favorite-placements`,
    ],
  });
  await queryClient.invalidateQueries({ queryKey: [fetchHiringFeedApiUrl(workerId)] });
  await queryClient.invalidateQueries({ queryKey: [getPlacementUrl(placementId, workerId)] });
  await queryClient.invalidateQueries({ queryKey: [fetchPlacementsApiUrl(workerId)] });
  await queryClient.invalidateQueries({ queryKey: [fetchDiscoveryPlacementsApiUrl(workerId)] });
}

export async function cancelPlacementFetchingQueries(
  queryClient: QueryClient,
  workerId: string,
  placementId: string
) {
  await queryClient.cancelQueries({
    queryKey: [
      `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/favorite-placements`,
    ],
  });
  await queryClient.cancelQueries({ queryKey: [fetchHiringFeedApiUrl(workerId)] });
  await queryClient.cancelQueries({ queryKey: [fetchPlacementsApiUrl(workerId)] });
  await queryClient.cancelQueries({ queryKey: [fetchDiscoveryPlacementsApiUrl(workerId)] });
  await queryClient.cancelQueries({ queryKey: [getPlacementUrl(placementId, workerId)] });
}

export function optimisticallySetPlacementFavoritedState(
  queryClient: QueryClient,
  workerId: string,
  placementId: string,
  newFavoritedState: boolean
) {
  // update the hiring-feed data
  queryClient.setQueriesData<InfiniteData<HiringFeedsResponse>>(
    [fetchHiringFeedApiUrl(workerId)],
    (oldData) => updatePaginatedPlacementsFavoriteState(oldData, placementId, newFavoritedState)
  );

  queryClient.setQueriesData<InfiniteData<GetPlacementsResponse>>(
    [fetchPlacementsApiUrl(workerId)],
    (oldData) => updatePaginatedPlacementsFavoriteState(oldData, placementId, newFavoritedState)
  );

  queryClient.setQueriesData<InfiniteData<DiscoveryPlacementsResponse>>(
    [fetchDiscoveryPlacementsApiUrl(workerId)],
    (oldData) => updatePaginatedPlacementsFavoriteState(oldData, placementId, newFavoritedState)
  );

  queryClient.setQueriesData<PlacementResponse>(
    [getPlacementUrl(placementId, workerId)],
    (oldData) => {
      if (!oldData) {
        return oldData;
      }

      return {
        ...oldData,
        data: {
          ...oldData.data,
          meta: {
            ...oldData.data.meta,
            isFavorited: newFavoritedState,
          },
        },
      };
    }
  );
}

function updatePaginatedPlacementsFavoriteState<
  T extends { data: Array<{ id: string; meta: { isFavorited?: boolean | undefined } }> },
>(
  oldData: InfiniteData<T> | undefined,
  placementId: string,
  favoritedState: boolean
): InfiniteData<T> | undefined {
  if (!oldData) {
    return oldData;
  }

  const newPages = oldData.pages.map((page) => {
    if (!page) {
      return page;
    }

    const newData = page.data.map((item) => {
      if (item.id === placementId) {
        return {
          ...item,
          meta: {
            ...item.meta,
            isFavorited: favoritedState,
          },
        };
      }

      return item;
    });

    return {
      ...page,
      data: newData,
    };
  });

  return { ...oldData, pages: newPages };
}

export function optimisticallySetPlacementWorkerFavoritePlacementId(
  queryClient: QueryClient,
  workerId: string,
  placementId: string,
  workerFavoritePlacementId: string
) {
  queryClient.setQueriesData<InfiniteData<HiringFeedsResponse>>(
    [fetchHiringFeedApiUrl(workerId)],
    (oldData) =>
      updatePaginatedPlacementsWorkerFavoritePlacementId(
        oldData,
        placementId,
        workerFavoritePlacementId
      )
  );

  queryClient.setQueriesData<InfiniteData<GetPlacementsResponse>>(
    [fetchPlacementsApiUrl(workerId)],
    (oldData) =>
      updatePaginatedPlacementsWorkerFavoritePlacementId(
        oldData,
        placementId,
        workerFavoritePlacementId
      )
  );

  queryClient.setQueriesData<InfiniteData<DiscoveryPlacementsResponse>>(
    [fetchDiscoveryPlacementsApiUrl(workerId)],
    (oldData) =>
      updatePaginatedPlacementsWorkerFavoritePlacementId(
        oldData,
        placementId,
        workerFavoritePlacementId
      )
  );

  queryClient.setQueriesData<PlacementResponse>(
    [getPlacementUrl(placementId, workerId)],
    (oldData) => {
      if (!oldData) {
        return oldData;
      }

      return {
        ...oldData,
        data: {
          ...oldData.data,
          relationships: {
            ...oldData.data.relationships,
            workerFavoritePlacement: {
              data: {
                type: API_TYPES.workerFavoritePlacement,
                id: workerFavoritePlacementId,
              },
            },
          },
        },
      };
    }
  );
}

function updatePaginatedPlacementsWorkerFavoritePlacementId<
  T extends {
    data: Array<{
      id: string;
      relationships: {
        workerFavoritePlacement?: { data: { type: string; id: string } };
      };
    }>;
  },
>(
  oldData: InfiniteData<T> | undefined,
  placementId: string,
  workerFavoritePlacementId: string
): InfiniteData<T> | undefined {
  if (!oldData) {
    return oldData;
  }

  const newPages = oldData.pages.map((page) => {
    if (!page) {
      return page;
    }

    const newData = page.data.map((item) => {
      if (item.id === placementId) {
        return {
          ...item,
          relationships: {
            ...item.relationships,
            workerFavoritePlacement: {
              data: {
                type: API_TYPES.workerFavoritePlacement,
                id: workerFavoritePlacementId,
              },
            },
          },
        };
      }

      return item;
    });

    return {
      ...page,
      data: newData,
    };
  });

  return { ...oldData, pages: newPages };
}
